/**
 * Games (`/b/:brandId/games`): the games this brand's places run, editable
 * (place manager, unit 8; `brand-games.ts`). Tick or untick, "Review" (one
 * line of only the changes), "Apply" (the full list), "Undo" for five
 * minutes. An Apply that leaves no catalog game showing needs a second tap
 * on the review line first. The state and every step live in the store
 * (`games-store.ts`); the body draws from it (`games-view.tsx`).
 *
 * The brand's owner and members, and staff; anyone else gets no page (and no
 * link in the sidebar). `?fixtures=1` (dev and preview) serves a sample brand,
 * picked above the list; nothing is sent anywhere.
 */
import * as React from "react";
import { useParams } from "react-router-dom";

import { usePortal } from "@/features/portal/portal-context";
import { PlacesError, PlacesLoading } from "@/features/places/places-page";
import { useFixtureMode } from "@/features/places/use-places";
import { undoOffered } from "@/features/writes/writes";
import { useNow } from "@/lib/use-now";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { GONE_LINE, canEditBrandGames } from "@/features/games/brand-games";
import { SAMPLE_BRANDS, type SampleBrandKey } from "@/features/games/fixtures";
import { tickedOf } from "@/features/games/games-store";
import { GamesView } from "@/features/games/games-view";
import { useBrandGames } from "@/features/games/use-brand-games";

/** What a viewer who is neither the brand's staff nor pop.in staff sees. */
export const NOT_YOURS_LINE = "The games a brand's places run are changed by the brand's owner and members.";

/** The brand does not read here; `gone`: an Apply or Undo just found so, and
 *  sent nothing (said above the card, which would otherwise replace the line). */
export function NoBrand({ gone }: { gone: boolean }) {
  return (
    <Card className="flex flex-col gap-1.5 p-5 text-[13px] text-muted-foreground">
      {gone && <p className="font-semibold text-failure">{GONE_LINE}</p>}
      <p>This brand doesn't exist, or you don't have access to it.</p>
    </Card>
  );
}

export function GamesPage() {
  // The URL is the source of truth for the brand (see `navigation.ts`).
  const { brandId = "" } = useParams();
  const { brands, isSuperAdmin } = usePortal();
  const brand = brands.find((b) => b.id === brandId);
  if (!canEditBrandGames({ isSuperAdmin, viewerRole: brand?.viewerRole })) {
    return (
      <div className="flex flex-col gap-5">
        <GamesHeading name={null} />
        <Card className="p-5 text-[13px] text-muted-foreground">{NOT_YOURS_LINE}</Card>
      </div>
    );
  }
  // One per brand: a brand left behind keeps no draft, review or Undo.
  return <GamesHost key={brandId} brandId={brandId} brandName={brand?.name ?? null} />;
}

function GamesHeading({ name }: { name: string | null }) {
  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-[-.02em]">Games</h1>
      <p className="mt-1 text-[13.5px] text-muted-foreground">
        The games {name ? `${name}'s` : "this brand's"} places run. The list is the brand's: every place of the brand runs it.
      </p>
    </div>
  );
}

function GamesHost({ brandId, brandName }: { brandId: string; brandName: string | null }) {
  const sampleMode = useFixtureMode();
  const [sampleBrand, setSampleBrand] = React.useState<SampleBrandKey>("parlor");
  return (
    <div className="flex flex-col gap-5">
      <GamesHeading name={sampleMode ? SAMPLE_BRANDS[sampleBrand].name : brandName} />
      {sampleMode && (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-text-3">
          Sample brand:
          {(Object.keys(SAMPLE_BRANDS) as SampleBrandKey[]).map((key) => (
            <Button key={key} size="sm" variant={key === sampleBrand ? "solid" : "subtle"} onClick={() => setSampleBrand(key)}>
              {SAMPLE_BRANDS[key].name}
            </Button>
          ))}
        </div>
      )}
      {/* One per sample: switching samples keeps no draft, review or Undo. */}
      <GamesEditor key={sampleBrand} brandId={brandId} sampleBrand={sampleBrand} />
    </div>
  );
}

function GamesEditor({ brandId, sampleBrand }: { brandId: string; sampleBrand: SampleBrandKey }) {
  const [state, store] = useBrandGames(brandId, sampleBrand);
  const { load, changed } = state;
  const read = load.state === "ready" ? load.data : null;
  // One set of ticks per draft or read, so the view's model is worked out once
  // per change, not on each tick of the clock below.
  const ticked = React.useMemo(() => tickedOf(state), [state.draft, read]);
  const clock = Date.now();
  // Tick once a second only while "Undo" is on screen (it goes at five minutes).
  useNow(changed !== null && undoOffered(changed, clock));

  if (load.state === "loading") return <PlacesLoading what="the games" />;
  if (load.state === "error") return <PlacesError title="Could not read the games" message={load.message} />;
  if (!read) {
    // An Apply or Undo just found the brand gone: say nothing was sent, above the card.
    const step = state.step;
    return <NoBrand gone={(step.kind === "edit" && step.notice === GONE_LINE) || (typeof changed?.undo === "object" && "gone" in changed.undo)} />;
  }
  return (
    <GamesView
      read={read}
      sample={load.sample}
      rereading={state.rereading}
      refreshFailed={load.refreshFailed}
      ticked={ticked}
      step={state.step}
      changed={changed}
      now={clock}
      onTick={store.tick}
      onReview={store.review}
      onDiscard={store.discard}
      onBack={store.back}
      onConfirmEmpty={store.confirmEmpty}
      onApply={() => void store.apply()}
      onClose={store.close}
      onUndo={() => void store.undo()}
    />
  );
}
